import { useEffect, useState } from 'react'
import { apiExtendBiasOverride, apiListBiasOverrides } from '../studioApi.js'
import {
  buildExpiryBannerMessages,
  getOverrideStatus,
} from '../biasOverrideUtils.js'

// useBiasOverrideBanner (extracted from App.jsx, tech-debt backlog P2 item 12).
//
// Owns the bias-override expiry banner: the live override list polled every
// 60s, the per-ID "extending…" in-flight set, and the dismissed-ID set
// persisted to localStorage. App.jsx keeps the GENERAL banner system
// (bannerMessages / addBanner / dismissBanner); this hook owns only the
// bias-specific state + effects + the quick-extend handler, and exposes:
//   - buildMessages(pillarLabel) → the bias banner rows for the render
//   - dismissOverride(overrideId) → called from App's dismissBanner when a
//     `bias-override-*` banner is dismissed (App still owns the banner-id
//     format; this hook just records the dismissal).
//
// Plain .js (not .ts like the other src/hooks): its sole consumer is the
// untyped App.jsx and the override objects are loosely-typed API payloads —
// this is a verbatim logic relocation, not a new typed surface. Behavior is
// unchanged; appBootstrap.test.jsx (renders App) exercises it.
//
// @param {{ authenticated: boolean, setToast: (msg: string) => void }} deps
export function useBiasOverrideBanner({ authenticated, setToast }) {
  // Bias override expiry surface (Wave 14). The banner is derived from
  // the live override list every 60s; dismissedIds persists in
  // localStorage so banners don't re-flood after a reload.
  const [biasOverridesForBanner, setBiasOverridesForBanner] = useState([])
  // Wave 16 — set of override IDs whose extend request is in flight, so
  // the banner button can show "Extending…" disabled state.
  const [biasExtendingIds, setBiasExtendingIds] = useState(() => new Set())
  const [biasBannerDismissed, setBiasBannerDismissed] = useState(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const stored = window.localStorage.getItem(
        'ignazio-studio-bias-banner-dismissed'
      )
      if (!stored) return new Set()
      const parsed = JSON.parse(stored)
      return new Set(Array.isArray(parsed) ? parsed : [])
    } catch {
      return new Set()
    }
  })

  // Record a dismissal. App's dismissBanner parses the override id out of
  // the `bias-override-*` banner id and calls this; bias-override banners
  // persist their dismissal across reloads so the operator isn't
  // re-prompted for the same expired override.
  function dismissOverride(overrideId) {
    if (!overrideId) return
    setBiasBannerDismissed((prev) => {
      if (prev.has(overrideId)) return prev
      const next = new Set(prev)
      next.add(overrideId)
      return next
    })
  }

  // Wave 16 — quick-extend a bias override directly from the expiry
  // banner. Optimistic UI: mark the ID in-flight so the button shows
  // "Extending…", call the API, then re-fetch the active list so the
  // new (successor) override surfaces and the banner row drops out.
  async function handleExtend(overrideId, days) {
    if (!overrideId) return
    setBiasExtendingIds((prev) => {
      if (prev.has(overrideId)) return prev
      const next = new Set(prev)
      next.add(overrideId)
      return next
    })
    try {
      await apiExtendBiasOverride(overrideId, { extendDays: days })
      setToast(`Extended bias override ${days}d`)
      // Re-fetch active overrides so the banner refreshes immediately.
      try {
        const data = await apiListBiasOverrides({ activeOnly: true })
        setBiasOverridesForBanner(data?.overrides || [])
      } catch {
        // Polling will catch it within 60s; not fatal.
      }
    } catch (error) {
      setToast(error?.message || 'Failed to extend override')
    } finally {
      setBiasExtendingIds((prev) => {
        if (!prev.has(overrideId)) return prev
        const next = new Set(prev)
        next.delete(overrideId)
        return next
      })
    }
  }

  // Poll the active override list every 60s. Keeps the loop honest: an
  // operator who pinned a 7-day override gets a session-level prompt 24h
  // before it decays + a stronger one when it crosses the line. Polling >
  // push because there's no active websocket and the cadence cost is
  // negligible (one cheap GET per minute).
  useEffect(() => {
    if (!authenticated) return undefined
    let cancelled = false
    async function refresh() {
      try {
        const data = await apiListBiasOverrides({ activeOnly: true })
        if (cancelled) return
        const overrides = data?.overrides || []
        // Anything with a status of 'active' (no expiry impending) doesn't
        // need a banner — only expired and expiring-soon do. We still pull
        // the full active list because the moment one transitions to
        // expired, it's still "active in DB" until revoked, but our pure
        // helper classifies it as 'expired' once expiresAt has passed.
        setBiasOverridesForBanner(overrides)
      } catch {
        // Silent — this is a background poll. A failed fetch shouldn't
        // banner-spam the operator.
      }
    }
    refresh()
    const interval = setInterval(refresh, 60_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [authenticated])

  // Persist dismissed override-banner IDs across reloads so the operator
  // doesn't see the same alert every page load.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        'ignazio-studio-bias-banner-dismissed',
        JSON.stringify([...biasBannerDismissed])
      )
    } catch {
      // localStorage can be quota-locked or disabled; just no-op.
    }
  }, [biasBannerDismissed])

  // Garbage-collect dismissed IDs whose underlying override is no longer
  // expiring/expired — keeps localStorage small and lets the operator see
  // the banner again if a future override happens to reuse an ID
  // (defensive; monotonicId IDs don't actually collide).
  useEffect(() => {
    if (biasBannerDismissed.size === 0) return
    const liveIds = new Set(
      biasOverridesForBanner
        .filter((o) => {
          const status = getOverrideStatus(o)
          return status === 'expired' || status === 'expiring-soon'
        })
        .map((o) => o.id)
    )
    let pruned = false
    const next = new Set()
    for (const id of biasBannerDismissed) {
      if (liveIds.has(id)) {
        next.add(id)
      } else {
        pruned = true
      }
    }
    if (pruned) setBiasBannerDismissed(next)
  }, [biasOverridesForBanner, biasBannerDismissed])

  // Build the bias banner rows for the render. pillarLabel resolves a
  // pillar id → human label (App owns the pillar registry).
  function buildMessages(pillarLabel) {
    return buildExpiryBannerMessages(biasOverridesForBanner, {
      dismissedIds: biasBannerDismissed,
      pillarLabel,
      onExtend: handleExtend,
      extendDays: 7,
      extendingIds: biasExtendingIds,
    })
  }

  return { buildMessages, dismissOverride }
}
